import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { customerService } from "../../services/customerService";

interface CurrencyBalance {
  currency: string;
  availableBalance: number;
  pendingBalance: number;
  totalCollected: number;
  totalWithdrawn: number;
}

interface AccountState {
  balances: CurrencyBalance[];
  selectedCurrency: string;
  errors: string | null;
  isLoading: boolean;
}

const initialState: AccountState = {
  balances: [],
  selectedCurrency: "XAF",
  errors: null,
  isLoading: false,
};

// Async thunks
export const fetchAllBalances = createAsyncThunk<
  CurrencyBalance[],
  void
>("account/fetchAllBalances", async () => {
  const response = await customerService.getBalance();
  return response.data; // API returns array of currency balances
});

export const fetchBalance = createAsyncThunk<number, string>(
  "account/fetchBalance",
  async (currency: string) => {
    const response = await customerService.getBalance();
    const balanceObj = response.data.find(
      (b: CurrencyBalance) => b.currency === currency
    );
    return balanceObj ? balanceObj.availableBalance : 0;
  }
);

const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    setSelectedCurrency: (state, action) => {
      state.selectedCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllBalances.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchAllBalances.fulfilled, (state, action) => {
        state.balances = action.payload;
        state.isLoading = false;
        
        // If no currency is selected yet, set to first available or XAF
        if (!state.selectedCurrency || state.balances.length > 0) {
          const xafBalance = state.balances.find(
            (b) => b.currency === "XAF"
          );
          if (xafBalance && !state.selectedCurrency) {
            state.selectedCurrency = "XAF";
          }
        }
      })
      .addCase(fetchAllBalances.rejected, (state, action) => {
        state.errors = action.error.message || "Failed to fetch balances";
        state.isLoading = false;
      })
      .addCase(fetchBalance.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        // Keep individual balance for backward compatibility if needed
        state.isLoading = false;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.errors = action.error.message || "Failed to fetch balance";
        state.isLoading = false;
      });
  },
});

export const { setSelectedCurrency } = accountSlice.actions;
export default accountSlice.reducer;